import jsPDF from 'jspdf';
import { format } from 'date-fns';

/**
 * Generate Enrollment Form PDF
 */
export async function generateEnrollmentPDF(enrollment, employee, employer, product, familyMembers = []) {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let yPos = 20;

  // Professional Header with Blue Box
  pdf.setFillColor(37, 99, 235); // Blue
  pdf.rect(0, 0, pageWidth, 35, 'F');
  
  pdf.setFontSize(22);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(255, 255, 255);
  pdf.text('SECTION 125 BENEFITS ENROLLMENT', pageWidth / 2, 16, { align: 'center' });
  
  pdf.setFontSize(11);
  pdf.setFont(undefined, 'normal');
  pdf.text((employer?.company_name || 'Marcom Benefits').toUpperCase(), pageWidth / 2, 26, { align: 'center' });
  
  // Reset text color
  pdf.setTextColor(0, 0, 0);
  yPos = 45;
  
  // Employee Information Section
  pdf.setFillColor(240, 249, 255); // Light blue background
  pdf.rect(15, yPos, pageWidth - 30, 38, 'F');
  pdf.setDrawColor(37, 99, 235);
  pdf.rect(15, yPos, pageWidth - 30, 38);
  
  yPos += 7;
  pdf.setFontSize(13);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(37, 99, 235);
  pdf.text('EMPLOYEE INFORMATION', 20, yPos);
  pdf.setTextColor(0, 0, 0);
  yPos += 8;
  
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  pdf.text(`Name: ${employee.first_name} ${employee.last_name}`, 20, yPos);
  pdf.text(`DOB: ${employee.date_of_birth ? format(new Date(employee.date_of_birth), 'MM/dd/yyyy') : 'N/A'}`, 120, yPos);
  yPos += 6;
  pdf.text(`SSN: XXX-XX-${employee.ssn_last_four || 'XXXX'}`, 20, yPos);
  pdf.text(`Phone: ${employee.phone || 'N/A'}`, 120, yPos);
  yPos += 6;
  pdf.text(`Email: ${employee.user_email}`, 20, yPos);
  yPos += 12;

  // Benefit Details Section
  pdf.setFillColor(236, 253, 245); // Light green background
  pdf.rect(15, yPos, pageWidth - 30, 38, 'F');
  pdf.setDrawColor(16, 185, 129);
  pdf.rect(15, yPos, pageWidth - 30, 38);
  
  yPos += 7;
  pdf.setFontSize(13);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(16, 185, 129);
  pdf.text('BENEFIT ENROLLMENT DETAILS', 20, yPos);
  pdf.setTextColor(0, 0, 0);
  yPos += 8;
  
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'bold');
  pdf.text(`Product: `, 20, yPos);
  pdf.setFont(undefined, 'normal');
  pdf.text(`${product?.name || 'N/A'}`, 45, yPos);
  yPos += 6;
  
  pdf.setFont(undefined, 'bold');
  pdf.text(`Coverage Level: `, 20, yPos);
  pdf.setFont(undefined, 'normal');
  const coverageText = (enrollment.coverage_level?.replaceAll('_', ' ') || 'Employee Only').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  pdf.text(coverageText, 55, yPos);
  yPos += 6;
  
  pdf.setFont(undefined, 'bold');
  pdf.text(`Monthly Premium: `, 20, yPos);
  pdf.setFont(undefined, 'normal');
  pdf.text(`$${(Number.parseFloat(enrollment.monthly_premium) || Number.parseFloat(enrollment.employee_cost) || 0).toFixed(2)}`, 60, yPos);
  pdf.setFont(undefined, 'bold');
  pdf.text(`Coverage Amount: `, 120, yPos);
  pdf.setFont(undefined, 'normal');
  pdf.text(`$${(Number.parseFloat(enrollment.coverage_amount) || 0).toLocaleString()}`, 165, yPos);
  yPos += 6;
  
  pdf.setFont(undefined, 'bold');
  pdf.text(`Effective Date: `, 20, yPos);
  pdf.setFont(undefined, 'normal');
  pdf.text(`${enrollment.effective_date ? format(new Date(enrollment.effective_date), 'MM/dd/yyyy') : 'Pending'}`, 55, yPos);
  yPos += 12;

  // Covered Family Members
  if (familyMembers.length > 0) {
    pdf.setFont(undefined, 'bold');
    pdf.text('Covered Family Members', 20, yPos);
    yPos += 8;
    
    pdf.setFont(undefined, 'normal');
    familyMembers.forEach(member => {
      pdf.text(`• ${member.first_name} ${member.last_name} (${member.relationship}) - DOB: ${format(new Date(member.date_of_birth), 'MM/dd/yyyy')}`, 20, yPos);
      yPos += 6;
    });
    yPos += 4;
  }

  // Tax Savings
  if (enrollment.estimated_tax_savings > 0) {
    yPos += 5;
    pdf.setFont(undefined, 'bold');
    pdf.text('Tax Benefits', 20, yPos);
    yPos += 8;
    
    pdf.setFont(undefined, 'normal');
    pdf.text(`Estimated Annual Tax Savings: $${enrollment.estimated_tax_savings.toFixed(2)}`, 20, yPos);
    yPos += 6;
    pdf.text(`Monthly Tax Savings: $${(enrollment.estimated_tax_savings / 12).toFixed(2)}`, 20, yPos);
    yPos += 10;
  }

  // Check if we need a new page for signature section
  if (yPos > pageHeight - 100) {
    pdf.addPage();
    yPos = 40;
  }

  // Signature
  yPos += 10;
  pdf.setFont(undefined, 'bold');
  pdf.text('Electronic Signature', 20, yPos);
  yPos += 8;
  
  pdf.setFont(undefined, 'normal');
  if (enrollment.signature_date) {
    pdf.text(`Signed on: ${format(new Date(enrollment.signature_date), 'MM/dd/yyyy hh:mm a')}`, 20, yPos);
    yPos += 6;
  }
  
  if (enrollment.signature_data) {
    try {
      pdf.addImage(enrollment.signature_data, 'PNG', 20, yPos, 60, 20);
      yPos += 25;
    } catch (e) {
      console.error('Failed to add signature:', e);
    }
  }

  // Professional Footer on all pages
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    
    pdf.setFillColor(37, 99, 235);
    pdf.rect(0, pageHeight - 25, pageWidth, 25, 'F');
    
    pdf.setFontSize(8);
    pdf.setTextColor(255, 255, 255);
    pdf.text('This is a legally binding enrollment document.', pageWidth / 2, pageHeight - 16, { align: 'center' });
    pdf.text(`Generated on: ${format(new Date(), 'MM/dd/yyyy hh:mm a')}`, pageWidth / 2, pageHeight - 11, { align: 'center' });
    pdf.text(`Document ID: ${enrollment.id.substring(0, 8).toUpperCase()}`, pageWidth / 2, pageHeight - 6, { align: 'center' });
    pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 30, pageHeight - 11);
  }

  return pdf;
}

/**
 * Generate Benefit Summary PDF for an employee
 */
export async function generateBenefitSummaryPDF(employee, enrollments, products, employer) {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let yPos = 20;

  // Professional Header with colored banner
  pdf.setFillColor(16, 185, 129); // Emerald color
  pdf.rect(0, 0, pageWidth, 35, 'F');
  
  pdf.setFontSize(24);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(255, 255, 255);
  pdf.text('BENEFITS SUMMARY', pageWidth / 2, 18, { align: 'center' });
  
  pdf.setFontSize(11);
  pdf.setFont(undefined, 'normal');
  pdf.text(employer?.company_name || 'Marcom Benefits', pageWidth / 2, 27, { align: 'center' });
  
  pdf.setTextColor(0, 0, 0);
  yPos = 50;

  // Employee Info Section with box
  pdf.setFillColor(240, 253, 245); // Light emerald background
  pdf.rect(15, yPos, pageWidth - 30, 30, 'F');
  pdf.setDrawColor(16, 185, 129);
  pdf.rect(15, yPos, pageWidth - 30, 30);
  
  yPos += 10;
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(15, 118, 110);
  pdf.text('EMPLOYEE INFORMATION', 20, yPos);
  pdf.setTextColor(0, 0, 0);
  
  yPos += 8;
  pdf.setFont(undefined, 'normal');
  pdf.text(`Name: ${employee.first_name} ${employee.last_name}`, 20, yPos);
  pdf.text(`Employee ID: ${employee.id.substring(0, 8)}`, 120, yPos);
  
  yPos += 6;
  pdf.text(`Generated: ${format(new Date(), 'MM/dd/yyyy')}`, 20, yPos);
  
  yPos += 20;

  // Active Benefits Section
  pdf.setFontSize(14);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(15, 118, 110);
  pdf.text('Your Active Benefits', 20, yPos);
  pdf.setTextColor(0, 0, 0);
  yPos += 8;

  const activeEnrollments = enrollments.filter(e => e.status === 'active');
  let totalPremium = 0;

  if (activeEnrollments.length === 0) {
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(100);
    pdf.text('No active benefits enrolled', 20, yPos);
    yPos += 15;
  } else {
    activeEnrollments.forEach((enrollment, idx) => {
      const product = products.find(p => p.id === enrollment.product_id);
      const premium = Number.parseFloat(enrollment.monthly_premium) || Number.parseFloat(enrollment.employee_cost) || 0;
      const coverage = Number.parseFloat(enrollment.coverage_amount) || 0;
      totalPremium += premium;
      
      // Benefit box
      pdf.setFillColor(249, 250, 251);
      pdf.rect(15, yPos, pageWidth - 30, 32, 'F');
      pdf.setDrawColor(226, 232, 240);
      pdf.rect(15, yPos, pageWidth - 30, 32);
      
      yPos += 8;
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'bold');
      pdf.text(`${idx + 1}. ${product?.name || 'Unknown Benefit'}`, 20, yPos);
      
      yPos += 7;
      pdf.setFontSize(9);
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(71, 85, 105);
      
      // Coverage level and amount
      const coverageLevel = enrollment.coverage_level?.replaceAll('_', ' ').toUpperCase() || 'EMPLOYEE ONLY';
      pdf.text(`Coverage: ${coverageLevel}`, 20, yPos);
      if (coverage > 0) {
        pdf.text(`Amount: $${coverage.toLocaleString()}`, 100, yPos);
      }
      
      yPos += 6;
      pdf.text(`Monthly Premium: $${premium.toFixed(2)}`, 20, yPos);
      pdf.text(`Effective: ${enrollment.effective_date ? format(new Date(enrollment.effective_date), 'MM/dd/yyyy') : 'Pending'}`, 100, yPos);
      
      pdf.setTextColor(0, 0, 0);
      yPos += 13;
    });
  }

  yPos += 5;

  // Check if we need a new page for financial summary (needs ~90 space including footer)
  if (yPos > pageHeight - 120) {
    pdf.addPage();
    yPos = 40;
  }

  // Financial Summary Section
  pdf.setFillColor(254, 243, 199); // Amber background
  pdf.rect(15, yPos, pageWidth - 30, 50, 'F');
  pdf.setDrawColor(245, 158, 11);
  pdf.rect(15, yPos, pageWidth - 30, 50);
  
  yPos += 10;
  pdf.setFontSize(12);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(146, 64, 14);
  pdf.text('MONTHLY FINANCIAL SUMMARY', 20, yPos);
  pdf.setTextColor(0, 0, 0);
  
  yPos += 10;
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  
  // Total premium in larger text
  pdf.setFont(undefined, 'bold');
  pdf.setFontSize(11);
  pdf.text('Total Monthly Premium:', 20, yPos);
  pdf.text(`$${totalPremium.toFixed(2)}`, pageWidth - 50, yPos);
  
  yPos += 8;
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  
  if (employee.flex_allocation || employee.optimal_flex_amount) {
    const flexAllocation = Number.parseFloat(employee.flex_allocation || employee.optimal_flex_amount) || 0;
    const flexBalance = flexAllocation - totalPremium;
    const maxClaimable = flexBalance * 0.86;
    
    pdf.text('Premium Allocation:', 20, yPos);
    pdf.text(`$${flexAllocation.toFixed(2)}`, pageWidth - 50, yPos);
    yPos += 6;
    
    pdf.text('Remaining Balance:', 20, yPos);
    pdf.text(`$${flexBalance.toFixed(2)}`, pageWidth - 50, yPos);
    yPos += 6;
    
    pdf.setFontSize(9);
    pdf.setTextColor(100);
    pdf.text('Max Claimable (86%):', 20, yPos);
    pdf.text(`$${maxClaimable.toFixed(2)}`, pageWidth - 50, yPos);
    pdf.setTextColor(0, 0, 0);
  }

  // Footer on all pages
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    
    pdf.setDrawColor(200);
    pdf.line(15, pageHeight - 25, pageWidth - 15, pageHeight - 25);
    
    pdf.setFontSize(8);
    pdf.setTextColor(120);
    pdf.setFont(undefined, 'normal');
    pdf.text('This is an official benefits summary. Please retain for your records.', pageWidth / 2, pageHeight - 18, { align: 'center' });
    pdf.text(`Document generated on ${format(new Date(), 'MMMM d, yyyy')} at ${format(new Date(), 'h:mm a')}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
    
    // Page number
    pdf.setFontSize(8);
    pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 25, pageHeight - 10);
  }

  return pdf;
}

/**
 * Generate Flex Proposal PDF
 */
export async function generateFlexProposalPDF(employee, optimalResults, taxAnalysis) {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  let yPos = 20;

  // Header
  pdf.setFontSize(18);
  pdf.setFont(undefined, 'bold');
  pdf.text('Section 125 Premium Proposal', pageWidth / 2, yPos, { align: 'center' });
  
  yPos += 15;

  // Employee Info
  pdf.setFontSize(12);
  pdf.setFont(undefined, 'bold');
  pdf.text('Employee Information', 20, yPos);
  yPos += 8;
  
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  pdf.text(`Name: ${employee.first_name} ${employee.last_name}`, 20, yPos);
  yPos += 6;
  pdf.text(`Annual Salary: $${(employee.annual_salary || 0).toLocaleString()}`, 20, yPos);
  yPos += 6;
  pdf.text(`Filing Status: ${employee.filing_status || 'Single'}`, 20, yPos);
  yPos += 6;
  pdf.text(`Dependents: ${employee.dependents || 0}`, 20, yPos);
  yPos += 10;

  // Recommendation
  pdf.setFont(undefined, 'bold');
  pdf.text('Recommendation', 20, yPos);
  yPos += 8;
  
  pdf.setFontSize(14);
  pdf.setTextColor(0, 128, 0);
  pdf.text(`Optimal Premium Amount: $${optimalResults?.optimalAmount || employee.optimal_flex_amount || 0}/month`, 20, yPos);
  yPos += 10;
  
  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);
  pdf.setFont(undefined, 'normal');

  // Tax Savings Breakdown
  if (taxAnalysis) {
    pdf.setFont(undefined, 'bold');
    pdf.text('Annual Tax Savings Breakdown', 20, yPos);
    yPos += 8;
    
    pdf.setFont(undefined, 'normal');
    pdf.text(`Federal Tax Savings: $${taxAnalysis.savings.federal.toFixed(2)}`, 20, yPos);
    yPos += 6;
    pdf.text(`State Tax Savings: $${taxAnalysis.savings.state.toFixed(2)}`, 20, yPos);
    yPos += 6;
    pdf.text(`FICA Savings: $${taxAnalysis.savings.fica.toFixed(2)}`, 20, yPos);
    yPos += 6;
    pdf.setFont(undefined, 'bold');
    pdf.text(`Total Annual Savings: $${taxAnalysis.savings.total.toFixed(2)}`, 20, yPos);
    yPos += 6;
    pdf.text(`Monthly Savings: $${taxAnalysis.savings.monthlySavings.toFixed(2)}`, 20, yPos);
    yPos += 10;
  }

  // Before/After Comparison
  if (taxAnalysis) {
    pdf.setFont(undefined, 'bold');
    pdf.text('Before vs After Section 125', 20, yPos);
    yPos += 8;
    
    pdf.setFont(undefined, 'normal');
    
    // Before
    pdf.text('Without Section 125:', 20, yPos);
    yPos += 6;
    pdf.text(`  Taxable Income: $${taxAnalysis.before.taxableIncome.toFixed(2)}`, 25, yPos);
    yPos += 6;
    pdf.text(`  Total Tax: $${taxAnalysis.before.totalTax.toFixed(2)}`, 25, yPos);
    yPos += 6;
    pdf.text(`  Take Home: $${taxAnalysis.before.takeHome.toFixed(2)}`, 25, yPos);
    yPos += 8;
    
    // After
    pdf.text('With Section 125:', 20, yPos);
    yPos += 6;
    pdf.text(`  Taxable Income: $${taxAnalysis.after.taxableIncome.toFixed(2)}`, 25, yPos);
    yPos += 6;
    pdf.text(`  Total Tax: $${taxAnalysis.after.totalTax.toFixed(2)}`, 25, yPos);
    yPos += 6;
    pdf.text(`  Take Home: $${taxAnalysis.after.takeHome.toFixed(2)}`, 25, yPos);
  }

  // Footer
  const pageHeight = pdf.internal.pageSize.getHeight();
  pdf.setFontSize(8);
  pdf.setTextColor(128);
  pdf.text(`Generated: ${format(new Date(), 'MM/dd/yyyy hh:mm a')}`, 20, pageHeight - 10);
  pdf.text('This is a proposal and not a guarantee of tax savings.', pageWidth / 2, pageHeight - 10, { align: 'center' });

  return pdf;
}

/**
 * Generate Payroll Deduction Report CSV
 */
export function generatePayrollCSV(employees, enrollments, products) {
  const rows = [
    ['Employee ID', 'Employee Name', 'SSN Last 4', 'Benefit', 'Monthly Premium', 'Per-Paycheck Deduction', 'Pay Frequency']
  ];

  employees.forEach(employee => {
    const empEnrollments = enrollments.filter(e => e.employee_id === employee.id && e.status === 'active');
    
    empEnrollments.forEach(enrollment => {
      const product = products.find(p => p.id === enrollment.product_id);
      const monthlyPremium = enrollment.monthly_premium || 0;
      
      // Calculate per-paycheck deduction
      const payPeriods = {
        weekly: 52,
        bi_weekly: 26,
        semi_monthly: 24,
        monthly: 12
      };
      const periods = payPeriods[employee.pay_frequency] || 26;
      const perPaycheck = (monthlyPremium * 12) / periods;
      
      rows.push([
        employee.id.substring(0, 8),
        `${employee.first_name} ${employee.last_name}`,
        employee.ssn_last_four || '',
        product?.name || 'Unknown',
        monthlyPremium.toFixed(2),
        perPaycheck.toFixed(2),
        employee.pay_frequency || 'bi_weekly'
      ]);
    });
  });

  // Convert to CSV string
  const csvContent = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
  return csvContent;
}

/**
 * Download PDF helper
 */
export function downloadPDF(pdf, filename) {
  pdf.save(filename);
}

/**
 * Download CSV helper
 */
export function downloadCSV(csvContent, filename) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * Generate W-4 Pre-filled PDF
 */
export async function generateW4PDF(employee) {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  let yPos = 15;

  // IRS Header with Form Number
  pdf.setFontSize(8);
  pdf.setFont(undefined, 'normal');
  pdf.text('Form W-4', 20, yPos);
  pdf.text('Department of the Treasury', pageWidth - 20, yPos, { align: 'right' });
  yPos += 4;
  pdf.text('(Rev. December 2024)', 20, yPos);
  pdf.text('Internal Revenue Service', pageWidth - 20, yPos, { align: 'right' });
  yPos += 8;

  // Main Title
  pdf.setFontSize(14);
  pdf.setFont(undefined, 'bold');
  pdf.text("Employee's Withholding Certificate", pageWidth / 2, yPos, { align: 'center' });
  yPos += 8;
  
  pdf.setFontSize(9);
  pdf.setFont(undefined, 'normal');
  pdf.text('Complete Form W-4 so that your employer can withhold the correct federal income tax from your pay.', pageWidth / 2, yPos, { align: 'center' });
  yPos += 12;

  // STEP 1: Personal Information
  pdf.setFontSize(11);
  pdf.setFont(undefined, 'bold');
  pdf.setFillColor(220, 220, 220);
  pdf.rect(15, yPos - 4, pageWidth - 30, 7, 'F');
  pdf.text('Step 1: Enter Personal Information', 20, yPos);
  yPos += 10;
  
  pdf.setFontSize(9);
  pdf.setFont(undefined, 'normal');
  pdf.text(`(a) First name and middle initial:`, 20, yPos);
  pdf.setFont(undefined, 'bold');
  pdf.text(employee.first_name, 75, yPos);
  pdf.setFont(undefined, 'normal');
  pdf.text(`Last name:`, 115, yPos);
  pdf.setFont(undefined, 'bold');
  pdf.text(employee.last_name, 135, yPos);
  yPos += 7;
  
  pdf.setFont(undefined, 'normal');
  pdf.text(`(b) Social security number:`, 20, yPos);
  pdf.setFont(undefined, 'bold');
  pdf.text(`XXX-XX-${employee.ssn_last_four || 'XXXX'}`, 70, yPos);
  yPos += 7;
  
  pdf.setFont(undefined, 'normal');
  pdf.text(`(c) Address:`, 20, yPos);
  pdf.setFont(undefined, 'bold');
  pdf.text(employee.address || '___________________________', 45, yPos);
  yPos += 7;
  
  pdf.setFont(undefined, 'normal');
  pdf.text(`    City or town, state, and ZIP code:`, 20, yPos);
  pdf.setFont(undefined, 'bold');
  const cityStateZip = `${employee.city || ''}, ${employee.state || ''} ${employee.zip_code || ''}`;
  pdf.text(cityStateZip.trim() || '___________________________', 75, yPos);
  yPos += 10;
  
  // Filing Status Checkboxes
  pdf.setFont(undefined, 'normal');
  pdf.text('(c) Single or Married filing separately', 20, yPos);
  pdf.rect(18, yPos - 3, 3, 3);
  if (employee.filing_status === 'single') pdf.text('X', 18.5, yPos);
  yPos += 6;
  
  pdf.text('    Married filing jointly or Qualifying surviving spouse', 20, yPos);
  pdf.rect(18, yPos - 3, 3, 3);
  if (employee.filing_status === 'married') pdf.text('X', 18.5, yPos);
  yPos += 6;
  
  pdf.text('    Head of household (Check only if you\'re unmarried and pay more than half the costs of keeping up a home for yourself and a qualifying individual.)', 20, yPos);
  pdf.rect(18, yPos - 3, 3, 3);
  if (employee.filing_status === 'head_of_household') pdf.text('X', 18.5, yPos);
  yPos += 12;

  // STEP 2: Multiple Jobs
  pdf.setFontSize(11);
  pdf.setFont(undefined, 'bold');
  pdf.setFillColor(220, 220, 220);
  pdf.rect(15, yPos - 4, pageWidth - 30, 7, 'F');
  pdf.text('Step 2: Multiple Jobs or Spouse Works', 20, yPos);
  yPos += 7;
  
  pdf.setFontSize(9);
  pdf.setFont(undefined, 'normal');
  pdf.text('Complete this step if you (1) hold more than one job at a time, or (2) are married filing jointly and your spouse', 20, yPos);
  yPos += 5;
  pdf.text('also works. If you (and your spouse) have a total of only two jobs, you may check the box in option (c).', 20, yPos);
  yPos += 8;
  
  pdf.rect(18, yPos - 3, 3, 3);
  pdf.text('(c) There are only two jobs total. Check here and fill out the worksheet on page 3.', 23, yPos);
  if (employee.w4_step2_multiple_jobs) pdf.text('X', 18.5, yPos);
  yPos += 12;

  // STEP 3: Dependents
  pdf.setFontSize(11);
  pdf.setFont(undefined, 'bold');
  pdf.setFillColor(220, 220, 220);
  pdf.rect(15, yPos - 4, pageWidth - 30, 7, 'F');
  pdf.text('Step 3: Claim Dependents', 20, yPos);
  yPos += 7;
  
  pdf.setFontSize(9);
  pdf.setFont(undefined, 'normal');
  pdf.text('If your total income will be $200,000 or less ($400,000 or less if married filing jointly):', 20, yPos);
  yPos += 7;
  
  pdf.text('Multiply the number of qualifying children under age 17 by $2,000', 20, yPos);
  pdf.text('$', 135, yPos);
  const step3Stored = employee.w4_step3_total !== null && employee.w4_step3_total !== undefined
    ? Number.parseFloat(employee.w4_step3_total)
    : null;
  const childCredit = Number.isFinite(step3Stored) ? step3Stored : (employee.dependents || 0) * 2000;
  pdf.setFont(undefined, 'bold');
  pdf.text(childCredit.toLocaleString(), 140, yPos);
  pdf.setFont(undefined, 'normal');
  yPos += 6;
  
  pdf.text('Multiply the number of other dependents by $500', 20, yPos);
  pdf.text('$', 135, yPos);
  pdf.text('0', 140, yPos);
  yPos += 6;
  
  pdf.text('Add the amounts above for total credit', 20, yPos);
  pdf.text('$', 135, yPos);
  pdf.setFont(undefined, 'bold');
  pdf.text(childCredit.toLocaleString(), 140, yPos);
  pdf.setFont(undefined, 'normal');
  yPos += 10;
  
  pdf.setFontSize(8);
  pdf.text(`Number of dependents claimed: ${employee.dependents || 0}`, 20, yPos);
  yPos += 12;

  // STEP 4: Other Adjustments
  pdf.setFontSize(11);
  pdf.setFont(undefined, 'bold');
  pdf.setFillColor(220, 220, 220);
  pdf.rect(15, yPos - 4, pageWidth - 30, 7, 'F');
  pdf.text('Step 4 (optional): Other Adjustments', 20, yPos);
  yPos += 7;
  
  pdf.setFontSize(9);
  pdf.setFont(undefined, 'normal');
  pdf.text('(a) Other income (not from jobs). If you want tax withheld for other income you', 20, yPos);
  yPos += 5;
  pdf.text('    expect this year that won\'t have withholding, enter the amount here', 20, yPos);
  pdf.text('$', 135, yPos);
  pdf.text('0', 140, yPos);
  yPos += 7;
  
  pdf.text('(b) Deductions. If you expect to claim deductions other than the standard deduction', 20, yPos);
  yPos += 5;
  pdf.text('    and want to reduce your withholding, use the Deductions Worksheet and enter result', 20, yPos);
  pdf.text('$', 135, yPos);
  pdf.text('0', 140, yPos);
  yPos += 7;
  
  pdf.text('(c) Extra withholding. Enter any additional tax you want withheld each pay period', 20, yPos);
  pdf.text('$', 135, yPos);
  const extraWithholding = employee.w4_step4c_extra_withholding !== null && employee.w4_step4c_extra_withholding !== undefined
    ? Number.parseFloat(employee.w4_step4c_extra_withholding)
    : 0;
  pdf.setFont(undefined, 'bold');
  pdf.text(Number(extraWithholding || 0).toFixed(2), 140, yPos);
  pdf.setFont(undefined, 'normal');
  yPos += 12;

  // STEP 5: Signature
  pdf.setFontSize(11);
  pdf.setFont(undefined, 'bold');
  pdf.setFillColor(220, 220, 220);
  pdf.rect(15, yPos - 4, pageWidth - 30, 7, 'F');
  pdf.text('Step 5: Sign Here', 20, yPos);
  yPos += 7;
  
  pdf.setFontSize(9);
  pdf.setFont(undefined, 'normal');
  pdf.text('Under penalties of perjury, I declare that this certificate, to the best of my knowledge and belief, is true, correct, and complete.', 20, yPos);
  yPos += 10;
  
  pdf.text("Employee's signature:", 20, yPos);
  pdf.line(55, yPos, 120, yPos);
  pdf.text('Date:', 125, yPos);
  pdf.line(137, yPos, 170, yPos);
  pdf.setFont(undefined, 'bold');
  pdf.text(format(new Date(), 'MM/dd/yyyy'), 140, yPos - 1);
  yPos += 12;

  // Employer Section
  pdf.setFontSize(11);
  pdf.setFont(undefined, 'bold');
  pdf.setFillColor(240, 240, 240);
  pdf.rect(15, yPos - 4, pageWidth - 30, 7, 'F');
  pdf.text('For Employer Use Only', 20, yPos);
  yPos += 7;
  
  pdf.setFontSize(9);
  pdf.setFont(undefined, 'normal');
  pdf.text("Employer's name and address:", 20, yPos);
  pdf.text('_________________________________', 70, yPos);
  yPos += 6;
  pdf.text('First date of employment:', 20, yPos);
  if (employee.hire_date) {
    pdf.setFont(undefined, 'bold');
    pdf.text(format(new Date(employee.hire_date), 'MM/dd/yyyy'), 70, yPos);
    pdf.setFont(undefined, 'normal');
  } else {
    pdf.text('_________________________________', 70, yPos);
  }

  // Footer with OMB and Privacy Act
  const pageHeight = pdf.internal.pageSize.getHeight();
  pdf.setFontSize(7);
  pdf.setTextColor(100);
  pdf.text('Form W-4 (2024)', 20, pageHeight - 15);
  pdf.text('This is a computer-generated pre-filled form. Please review, sign, and submit to your employer.', pageWidth / 2, pageHeight - 12, { align: 'center' });
  pdf.text(`Generated: ${format(new Date(), 'MM/dd/yyyy hh:mm a')}`, pageWidth / 2, pageHeight - 8, { align: 'center' });

  return pdf;
}

/**
 * Generate Carrier Enrollment Packet PDF
 */
export async function generateCarrierPacketPDF(employee, enrollments, products, familyMembers, employer) {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  let yPos = 20;

  // Cover Page
  pdf.setFontSize(20);
  pdf.setFont(undefined, 'bold');
  pdf.text('Carrier Enrollment Packet', pageWidth / 2, yPos, { align: 'center' });
  yPos += 10;
  pdf.setFontSize(12);
  pdf.setFont(undefined, 'normal');
  pdf.text(employer?.company_name || 'Marcom Benefits', pageWidth / 2, yPos, { align: 'center' });
  yPos += 20;

  // Employee Information
  pdf.setFontSize(14);
  pdf.setFont(undefined, 'bold');
  pdf.text('Employee Information', 20, yPos);
  yPos += 10;

  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  const employeeInfo = [
    ['Name:', `${employee.first_name} ${employee.last_name}`],
    ['Date of Birth:', employee.date_of_birth ? format(new Date(employee.date_of_birth), 'MM/dd/yyyy') : 'N/A'],
    ['Gender:', employee.gender || 'N/A'],
    ['SSN:', `XXX-XX-${employee.ssn_last_four || 'XXXX'}`],
    ['Email:', employee.user_email],
    ['Phone:', employee.phone || 'N/A'],
    ['Address:', `${employee.address || ''}, ${employee.city || ''}, ${employee.state || ''} ${employee.zip_code || ''}`],
    ['Hire Date:', employee.hire_date ? format(new Date(employee.hire_date), 'MM/dd/yyyy') : 'N/A'],
    ['Employment Status:', employee.employment_status || 'full_time']
  ];

  employeeInfo.forEach(([label, value]) => {
    pdf.text(`${label} ${value}`, 20, yPos);
    yPos += 6;
  });

  yPos += 10;

  // Family Members
  if (familyMembers.length > 0) {
    pdf.setFont(undefined, 'bold');
    pdf.text('Covered Family Members', 20, yPos);
    yPos += 8;

    pdf.setFont(undefined, 'normal');
    familyMembers.forEach((member, idx) => {
      pdf.text(`${idx + 1}. ${member.first_name} ${member.last_name}`, 20, yPos);
      yPos += 6;
      pdf.text(`   Relationship: ${member.relationship} | DOB: ${format(new Date(member.date_of_birth), 'MM/dd/yyyy')} | Gender: ${member.gender}`, 25, yPos);
      yPos += 8;
    });
  }

  yPos += 10;

  // Enrolled Benefits
  pdf.setFont(undefined, 'bold');
  pdf.text('Enrolled Benefits', 20, yPos);
  yPos += 8;

  pdf.setFont(undefined, 'normal');
  enrollments.forEach((enrollment, idx) => {
    const product = products.find(p => p.id === enrollment.product_id);
    pdf.text(`${idx + 1}. ${product?.name || 'Unknown'}`, 20, yPos);
    yPos += 6;
    pdf.text(`   Coverage Level: ${enrollment.coverage_level?.replace('_', ' ')} | Premium: $${(enrollment.monthly_premium || 0).toFixed(2)}/mo`, 25, yPos);
    yPos += 6;
    pdf.text(`   Effective Date: ${enrollment.effective_date ? format(new Date(enrollment.effective_date), 'MM/dd/yyyy') : 'Pending'}`, 25, yPos);
    yPos += 8;

    if (yPos > 250) {
      pdf.addPage();
      yPos = 20;
    }
  });

  // Footer
  const pageHeight = pdf.internal.pageSize.getHeight();
  pdf.setFontSize(8);
  pdf.setTextColor(128);
  pdf.text(`Generated: ${format(new Date(), 'MM/dd/yyyy hh:mm a')}`, 20, pageHeight - 10);
  pdf.text('Carrier Enrollment Packet', pageWidth - 20, pageHeight - 10, { align: 'right' });

  return pdf;
}

/**
 * Generate Bulk Enrollment PDF with all benefits, agreement, and HR contact
 */
export async function generateBulkEnrollmentPDF(enrollments, employee, employer, products, familyMembers = [], signatureData = null) {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let yPos = 20;

  // Professional Header with Blue Box
  pdf.setFillColor(37, 99, 235);
  pdf.rect(0, 0, pageWidth, 35, 'F');
  
  pdf.setFontSize(22);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(255, 255, 255);
  pdf.text('SECTION 125 BENEFITS ENROLLMENT', pageWidth / 2, 16, { align: 'center' });
  
  pdf.setFontSize(11);
  pdf.setFont(undefined, 'normal');
  pdf.text((employer?.company_name || 'Marcom Benefits').toUpperCase(), pageWidth / 2, 26, { align: 'center' });
  
  pdf.setTextColor(0, 0, 0);
  yPos = 45;
  
  // Employee Information Section
  pdf.setFillColor(240, 249, 255);
  pdf.rect(15, yPos, pageWidth - 30, 38, 'F');
  pdf.setDrawColor(37, 99, 235);
  pdf.rect(15, yPos, pageWidth - 30, 38);
  
  yPos += 7;
  pdf.setFontSize(13);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(37, 99, 235);
  pdf.text('EMPLOYEE INFORMATION', 20, yPos);
  pdf.setTextColor(0, 0, 0);
  yPos += 8;
  
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  pdf.text(`Name: ${employee.first_name} ${employee.last_name}`, 20, yPos);
  pdf.text(`DOB: ${employee.date_of_birth ? format(new Date(employee.date_of_birth), 'MM/dd/yyyy') : 'N/A'}`, 120, yPos);
  yPos += 6;
  pdf.text(`SSN: XXX-XX-${employee.ssn_last_four || 'XXXX'}`, 20, yPos);
  pdf.text(`Phone: ${employee.phone || 'N/A'}`, 120, yPos);
  yPos += 6;
  pdf.text(`Email: ${employee.user_email}`, 20, yPos);
  yPos += 12;

  // Enrolled Benefits Section - Title
  pdf.setFontSize(13);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(16, 185, 129);
  pdf.text('ENROLLED BENEFITS', 20, yPos);
  pdf.setTextColor(0, 0, 0);
  yPos += 10;
  
  pdf.setFontSize(10);
  let totalMonthly = 0;
  
  enrollments.forEach((enrollment, idx) => {
    const product = products.find(p => p.id === enrollment.product_id);
    if (product) {
      // Check if benefit box will fit on page
      if (yPos > pageHeight - 100) {
        pdf.addPage();
        yPos = 40;
      }
      
      // Individual benefit box
      pdf.setFillColor(236, 253, 245);
      pdf.rect(15, yPos, pageWidth - 30, 28, 'F');
      pdf.setDrawColor(16, 185, 129);
      pdf.rect(15, yPos, pageWidth - 30, 28);
      
      yPos += 8;
      pdf.setFont(undefined, 'bold');
      pdf.text(`${idx + 1}. ${product.name}`, 20, yPos);
      yPos += 6;
      pdf.setFont(undefined, 'normal');
      pdf.text(`Coverage: ${enrollment.coverage_level?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' + ')}`, 20, yPos);
      yPos += 6;
      pdf.text(`Monthly Premium: $${Number.parseFloat(enrollment.monthly_premium).toFixed(2)}`, 20, yPos);
      pdf.text(`Effective: ${format(new Date(enrollment.effective_date), 'MM/dd/yyyy')}`, 120, yPos);
      yPos += 12;
      totalMonthly += Number.parseFloat(enrollment.monthly_premium);
    }
  });
  
  yPos += 5;
  pdf.setFont(undefined, 'bold');
  pdf.setFontSize(11);
  pdf.text(`Total Monthly Premium: $${totalMonthly.toFixed(2)}`, 20, yPos);
  yPos += 20;

  // Paycheck Impact Section
  if (yPos > pageHeight - 110) {
    pdf.addPage();
    yPos = 40;
  }
  
  pdf.setFillColor(255, 251, 235);
  pdf.rect(15, yPos, pageWidth - 30, 70, 'F');
  pdf.setDrawColor(245, 158, 11);
  pdf.rect(15, yPos, pageWidth - 30, 70);
  
  yPos += 10;
  pdf.setFontSize(13);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(245, 158, 11);
  pdf.text('PAYCHECK IMPACT ANALYSIS', 20, yPos);
  pdf.setTextColor(0, 0, 0);
  yPos += 10;
  
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  
  // Calculate annual premium
  const annualPremium = totalMonthly * 12;
  
  // Estimate tax savings (roughly 30% of premium)
  const estimatedTaxSavings = annualPremium * 0.3;
  const monthlySavings = estimatedTaxSavings / 12;
  const effectiveCost = totalMonthly - monthlySavings;
  
  pdf.text(`Monthly Premium (Pre-Tax): $${totalMonthly.toFixed(2)}`, 20, yPos);
  yPos += 7;
  pdf.text(`Estimated Tax Savings: -$${monthlySavings.toFixed(2)}`, 20, yPos);
  yPos += 7;
  pdf.setFont(undefined, 'bold');
  pdf.text(`Effective Cost to You: $${effectiveCost.toFixed(2)}/month`, 20, yPos);
  yPos += 10;
  
  pdf.setFont(undefined, 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(100);
  pdf.text('Annual Tax Savings: $' + estimatedTaxSavings.toFixed(0), 20, yPos);
  yPos += 5;
  pdf.text('Savings based on estimated 30% tax rate. Actual savings may vary.', 20, yPos);
  pdf.setTextColor(0, 0, 0);
  yPos += 15;

  // ===== PAGE 2: AGREEMENT =====
  pdf.addPage();
  yPos = 20;
  
  // Agreement Header
  pdf.setFillColor(37, 99, 235);
  pdf.rect(0, 0, pageWidth, 25, 'F');
  pdf.setFontSize(18);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(255, 255, 255);
  pdf.text('ENROLLMENT AGREEMENT', pageWidth / 2, 15, { align: 'center' });
  pdf.setTextColor(0, 0, 0);
  yPos = 35;
  
  // Agreement Content
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  
  const agreementText = [
    'By signing this enrollment form, I acknowledge and agree to the following:',
    '',
    '1. ACCURACY OF INFORMATION: I certify that all information provided is true, complete, and accurate.',
    '',
    '2. PREMIUM DEDUCTIONS: I authorize payroll deductions for the premiums shown above on a pre-tax',
    '   basis under Section 125 of the Internal Revenue Code.',
    '',
    '3. TAX IMPLICATIONS: I understand pre-tax deductions reduce my taxable income and have consulted',
    '   or been advised to consult with a tax professional regarding the implications.',
    '',
    '4. BINDING ELECTIONS: My benefit elections are binding for the plan year and can only be changed',
    '   during open enrollment or following a qualifying life event as defined by IRS regulations.',
    '',
    '5. PLAN DOCUMENTS: I have access to and reviewed the Summary Plan Descriptions and understand',
    '   the official plan documents govern my coverage.',
    '',
    '6. COVERAGE EFFECTIVE DATE: Coverage becomes effective on the date specified by my employer,',
    '   subject to any waiting periods and carrier approval.',
    ''
  ];
  
  agreementText.forEach(line => {
    if (yPos > pageHeight - 30) {
      pdf.addPage();
      yPos = 20;
    }
    pdf.text(line, 20, yPos);
    yPos += 6;
  });
  
  yPos += 10;
  
  // Check if signature section will fit
  if (yPos > pageHeight - 90) {
    pdf.addPage();
    yPos = 40;
  }

  // Signature Section
  pdf.setFillColor(240, 249, 255);
  pdf.rect(15, yPos, pageWidth - 30, 50, 'F');
  pdf.setDrawColor(37, 99, 235);
  pdf.rect(15, yPos, pageWidth - 30, 50);
  
  yPos += 8;
  pdf.setFont(undefined, 'bold');
  pdf.text('EMPLOYEE SIGNATURE', 20, yPos);
  yPos += 8;
  
  pdf.setFont(undefined, 'normal');
  const signedDate = enrollments[0]?.signed_at ? format(new Date(enrollments[0].signed_at), 'MM/dd/yyyy hh:mm a') : format(new Date(), 'MM/dd/yyyy hh:mm a');
  pdf.text(`Signed Date: ${signedDate}`, 20, yPos);
  yPos += 6;
  pdf.text(`Signature Method: Electronic`, 20, yPos);
  yPos += 10;
  
  // Add signature image if available
  if (signatureData) {
    try {
      pdf.addImage(signatureData, 'PNG', 20, yPos, 80, 20);
    } catch (err) {
      console.error('Error adding signature to PDF:', err);
    }
  }
  
  yPos += 25;
  pdf.setFontSize(8);
  pdf.setTextColor(100);
  pdf.text(`Electronically signed by ${employee.first_name} ${employee.last_name}`, 20, yPos);
  
  // ===== PAGE 3: HR CONTACT INFORMATION =====
  pdf.addPage();
  yPos = 30;
  
  // Header
  pdf.setFillColor(37, 99, 235);
  pdf.rect(0, 0, pageWidth, 25, 'F');
  pdf.setFontSize(18);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(255, 255, 255);
  pdf.text('CONTACT INFORMATION', pageWidth / 2, 15, { align: 'center' });
  pdf.setTextColor(0, 0, 0);
  
  // HR Contact Section
  pdf.setFillColor(236, 253, 245);
  pdf.rect(15, yPos, pageWidth - 30, 80, 'F');
  pdf.setDrawColor(16, 185, 129);
  pdf.rect(15, yPos, pageWidth - 30, 80);
  
  yPos += 10;
  pdf.setFontSize(13);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(16, 185, 129);
  pdf.text('HUMAN RESOURCES DEPARTMENT', 20, yPos);
  pdf.setTextColor(0, 0, 0);
  yPos += 12;
  
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  pdf.text(`Company: ${employer?.company_name || 'Marcom Benefits'}`, 20, yPos);
  yPos += 8;
  
  if (employer?.address) {
    pdf.text(`Address: ${employer.address}`, 20, yPos);
    yPos += 6;
    if (employer.city && employer.state && employer.zip_code) {
      pdf.text(`         ${employer.city}, ${employer.state} ${employer.zip_code}`, 20, yPos);
      yPos += 8;
    }
  } else {
    yPos += 8;
  }
  
  if (employer?.phone) {
    pdf.text(`Phone: ${employer.phone}`, 20, yPos);
    yPos += 6;
  }
  
  if (employer?.email) {
    pdf.text(`Email: ${employer.email}`, 20, yPos);
    yPos += 6;
  }
  
  yPos = 130; // Fixed position for questions section
  
  // Questions Section
  pdf.setFontSize(12);
  pdf.setFont(undefined, 'bold');
  pdf.text('Questions About Your Benefits?', 20, yPos);
  yPos += 10;
  
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  const helpText = [
    'If you have questions about your enrollment or benefits, please contact:',
    '',
    '• Your HR Department using the contact information above',
    '• Your benefits portal at ' + (globalThis.location?.origin || 'your benefits website'),
    '• Review your Summary Plan Descriptions (SPDs) for detailed benefit information'
  ];
  
  helpText.forEach(line => {
    pdf.text(line, 20, yPos);
    yPos += 6;
  });
  
  // Footer on all pages
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(128);
    pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    pdf.text(`Generated: ${format(new Date(), 'MM/dd/yyyy hh:mm a')}`, 20, pageHeight - 10);
  }
  
  return pdf;
}

/**
 * Shared helpers for the multi-page Proposal PDF report
 */
function pdfMoney(value) {
  const num = Number(value) || 0;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function pdfPercent(value) {
  const num = Number(value) || 0;
  return `${num.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function drawProposalPageChrome(pdf, pageWidth, pageHeight, title, proposal) {
  pdf.setFillColor(15, 118, 110);
  pdf.rect(0, 0, pageWidth, 16, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(12);
  pdf.setFont(undefined, 'bold');
  pdf.text(title, 10, 11);
  pdf.setFontSize(8);
  pdf.setFont(undefined, 'normal');
  pdf.text(proposal.company_name || 'Employer', pageWidth - 10, 11, { align: 'right' });
  pdf.setTextColor(0, 0, 0);
}

function drawProposalFooter(pdf, pageWidth, pageHeight) {
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(7);
    pdf.setTextColor(120);
    pdf.text(`Generated: ${format(new Date(), 'MM/dd/yyyy hh:mm a')}`, 10, pageHeight - 6);
    pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 10, pageHeight - 6, { align: 'right' });
    pdf.setTextColor(0, 0, 0);
  }
}

function drawSectionHeading(pdf, text, x, y) {
  pdf.setFontSize(11);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(15, 118, 110);
  pdf.text(text, x, y);
  pdf.setTextColor(0, 0, 0);
  pdf.setFont(undefined, 'normal');
  return y + 6;
}

function drawStatCards(pdf, cards, x, y, totalWidth) {
  const gap = 4;
  const boxWidth = (totalWidth - gap * (cards.length - 1)) / cards.length;
  const boxHeight = 18;
  cards.forEach((card, idx) => {
    const bx = x + idx * (boxWidth + gap);
    pdf.setFillColor(248, 250, 252);
    pdf.rect(bx, y, boxWidth, boxHeight, 'F');
    pdf.setDrawColor(226, 232, 240);
    pdf.rect(bx, y, boxWidth, boxHeight);
    pdf.setFontSize(11);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(15, 23, 42);
    pdf.text(String(card.value), bx + boxWidth / 2, y + 9, { align: 'center' });
    pdf.setFontSize(7);
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(100, 116, 139);
    pdf.text(card.label, bx + boxWidth / 2, y + 15, { align: 'center' });
    pdf.setTextColor(0, 0, 0);
  });
  return y + boxHeight + 8;
}

/**
 * Truncates text with an ellipsis so it never exceeds maxWidth, instead of
 * relying on jsPDF's text() maxWidth option, which wraps onto a second line
 * that then overlaps the fixed-height row below it.
 */
function fitText(pdf, text, maxWidth) {
  const str = String(text ?? '');
  if (maxWidth <= 0 || pdf.getTextWidth(str) <= maxWidth) return str;
  let truncated = str;
  while (truncated.length > 1 && pdf.getTextWidth(`${truncated}…`) > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return `${truncated}…`;
}

/**
 * Draws a simple bordered table with pagination and a repeated header row.
 * headers: [{ label, align }], colWidths: number[] (mm), rows: string[][]
 * Returns the y position after the table.
 */
function drawTable(pdf, { x, y, pageWidth, pageHeight, headers, colWidths, rows, totalsRow, title }) {
  const rowHeight = 6;
  const fontSize = 7.5;
  const totalWidth = colWidths.reduce((a, b) => a + b, 0);
  let yPos = y;

  if (title) {
    yPos = drawSectionHeading(pdf, title, x, yPos);
  }

  const drawHeaderRow = () => {
    pdf.setFillColor(241, 245, 249);
    pdf.rect(x, yPos, totalWidth, rowHeight, 'F');
    pdf.setFont(undefined, 'bold');
    pdf.setFontSize(fontSize);
    pdf.setTextColor(30, 41, 59);
    let xPos = x;
    headers.forEach((h, i) => {
      const align = h.align || 'left';
      const tx = align === 'right' ? xPos + colWidths[i] - 2 : xPos + 2;
      pdf.text(fitText(pdf, h.label, colWidths[i] - 2), tx, yPos + rowHeight - 1.8, { align });
      xPos += colWidths[i];
    });
    pdf.setTextColor(0, 0, 0);
    pdf.setFont(undefined, 'normal');
    yPos += rowHeight;
  };

  const ensureSpace = () => {
    if (yPos + rowHeight > pageHeight - 14) {
      pdf.addPage();
      yPos = 20;
      drawHeaderRow();
    }
  };

  drawHeaderRow();

  rows.forEach((row, rIdx) => {
    ensureSpace();
    if (rIdx % 2 === 1) {
      pdf.setFillColor(248, 250, 252);
      pdf.rect(x, yPos, totalWidth, rowHeight, 'F');
    }
    let xPos = x;
    pdf.setFontSize(fontSize);
    row.forEach((cell, cIdx) => {
      const align = headers[cIdx]?.align || 'left';
      const tx = align === 'right' ? xPos + colWidths[cIdx] - 2 : xPos + 2;
      pdf.text(fitText(pdf, cell, colWidths[cIdx] - 2), tx, yPos + rowHeight - 1.8, { align });
      xPos += colWidths[cIdx];
    });
    yPos += rowHeight;
  });

  if (totalsRow) {
    ensureSpace();
    pdf.setFillColor(226, 232, 240);
    pdf.rect(x, yPos, totalWidth, rowHeight, 'F');
    pdf.setFont(undefined, 'bold');
    let xPos = x;
    totalsRow.forEach((cell, cIdx) => {
      const align = headers[cIdx]?.align || 'left';
      const tx = align === 'right' ? xPos + colWidths[cIdx] - 2 : xPos + 2;
      pdf.text(fitText(pdf, cell, colWidths[cIdx] - 2), tx, yPos + rowHeight - 1.8, { align });
      xPos += colWidths[cIdx];
    });
    pdf.setFont(undefined, 'normal');
    yPos += rowHeight;
  }

  return yPos + 6;
}

/**
 * Generate Proposal PDF — mirrors every section shown on the public "View Proposal"
 * page (proposal.census_rows computed data + proposal_summary), so the download
 * matches what a prospect sees at /proposal/:token.
 */
export async function generateProposalPDF(proposal, rows = []) {
  const pdf = new jsPDF({ orientation: 'landscape' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginX = 10;
  const contentWidth = pageWidth - marginX * 2;

  const summary = proposal.proposal_summary || {};
  const employeeName = (row, idx) => row.full_name || row.raw_data?.full_name || row.raw_data?.name || `Employee ${idx + 1}`;

  const sum = (key) => rows.reduce((total, row) => total + (Number(row[key]) || 0), 0);
  const monthlyFederal = sum('federal_tax_savings');
  const monthlyFica = sum('fica_tax_savings');
  const monthlyState = sum('state_tax_savings');
  const monthlyCombined = monthlyFederal + monthlyFica + monthlyState;
  const annualCombined = monthlyCombined * 12;
  const federalPct = annualCombined > 0 ? ((monthlyFederal * 12) / annualCombined) * 100 : 0;
  const ficaPct = annualCombined > 0 ? ((monthlyFica * 12) / annualCombined) * 100 : 0;
  const statePct = annualCombined > 0 ? ((monthlyState * 12) / annualCombined) * 100 : 0;
  const monthlyFee = sum('fee');
  const monthlyContrib = sum('contribution');
  const monthlyEmployerNet = sum('employer_net');
  const monthlyTotalTaxSavings = sum('total_tax_savings') || monthlyCombined;

  // ===== PAGE 1: Header, Company Info, Average Employee Impact, Key Metrics, Employees =====
  drawProposalPageChrome(pdf, pageWidth, pageHeight, 'BENEFITS PROPOSAL', proposal);

  let yPos = 24;
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'bold');
  pdf.text(proposal.company_name || 'Employer', marginX, yPos);
  pdf.setFont(undefined, 'normal');
  pdf.setFontSize(9);
  const location = [proposal.company_city, proposal.company_state, proposal.company_zipcode].filter(Boolean).join(', ');
  const contactBits = [proposal.contact_email, proposal.contact_phone].filter(Boolean).join('  •  ');
  pdf.text([proposal.company_address, location, contactBits].filter(Boolean).join('   |   '), marginX, yPos + 5);
  pdf.text(`Status: ${(proposal.status || 'proposed').toUpperCase()}`, pageWidth - marginX, yPos, { align: 'right' });
  yPos += 13;

  const avgMonthlyFlex = Number(summary.estimatedAvgMonthlyFlex) || 0;
  const avgFederalSavings = Number(summary.estimatedAvgMonthlyFederalTaxSavings) || 0;
  const avgFicaSavings = Number(summary.estimatedAvgMonthlyFicaTaxSavings) || 0;
  const hasSummaryTaxSplit = avgFederalSavings > 0 || avgFicaSavings > 0;
  const estimatedFederalAndFica = avgMonthlyFlex * 0.2965;
  const displayFederal = hasSummaryTaxSplit ? avgFederalSavings : estimatedFederalAndFica * 0.72;
  const displayFica = hasSummaryTaxSplit ? avgFicaSavings : estimatedFederalAndFica * 0.28;

  yPos = drawSectionHeading(pdf, 'Average Employee Impact', marginX, yPos);
  yPos = drawStatCards(pdf, [
    { label: 'Monthly Premium Credit', value: pdfMoney(avgMonthlyFlex) },
    { label: 'Federal Tax Savings', value: pdfMoney(displayFederal) },
    { label: 'FICA Savings', value: pdfMoney(displayFica) },
  ], marginX, yPos, contentWidth);

  yPos = drawSectionHeading(pdf, 'Key Metrics', marginX, yPos);
  yPos = drawStatCards(pdf, [
    { label: 'Total Employees', value: String(summary.totalRows || rows.length || 0) },
    { label: 'Monthly Net Savings', value: pdfMoney(summary.estimatedNetMonthlySavings) },
    { label: 'Annual Net Savings', value: pdfMoney(summary.estimatedNetAnnualSavings) },
    { label: 'Avg Monthly Premium', value: pdfMoney(summary.estimatedAvgMonthlyFlex) },
  ], marginX, yPos, contentWidth);

  yPos = drawTable(pdf, {
    x: marginX,
    y: yPos,
    pageWidth,
    pageHeight,
    title: 'Employees',
    headers: [
      { label: 'Employee', align: 'left' },
      { label: 'Salary', align: 'right' },
      { label: 'NW4', align: 'center' },
      { label: 'F', align: 'center' },
      { label: '#', align: 'center' },
      { label: '$', align: 'right' },
      { label: 'State', align: 'center' },
      { label: 'Sec125/mo', align: 'right' },
      { label: 'Net/mo', align: 'right' },
      { label: 'Empr Gross', align: 'right' },
      { label: 'Fee', align: 'right' },
      { label: 'Contrib', align: 'right' },
      { label: 'Empr Net', align: 'right' },
      { label: 'Annualized', align: 'right' },
    ],
    colWidths: [48, 18, 9, 7, 7, 13, 11, 17, 15, 17, 11, 15, 17, 17],
    rows: rows.map((row, idx) => {
      const fs = String(row.filing_status || '').toLowerCase();
      const fCode = row.filing_status_code || (fs.startsWith('m') ? 'M' : fs.startsWith('h') ? 'H' : fs ? 'S' : '-');
      const isPost2020 = row.w4_version === 'post_2020' || row.nw4 === 'Y';
      return [
        employeeName(row, idx),
        row.annual_salary ? pdfMoney(row.annual_salary) : '-',
        isPost2020 ? 'Y' : 'N',
        fCode,
        isPost2020 ? '-' : (row.w4_allowances ?? '-'),
        Number(row.w4_step3_total) > 0 ? pdfMoney(row.w4_step3_total) : '-',
        (row.state || '-').toUpperCase(),
        Number(row.flex_allocation) > 0 ? pdfMoney(row.flex_allocation) : '-',
        // Gated on eligibility (flex_allocation), not each figure's own sign —
        // net_flex/employer_net can legitimately be zero or negative and that
        // real number must print, not be hidden as '-'.
        Number(row.flex_allocation) > 0 ? pdfMoney(row.net_flex) : '-',
        Number(row.employer_gross) !== 0 ? pdfMoney(row.employer_gross) : '-',
        Number(row.fee) > 0 ? pdfMoney(row.fee) : '-',
        Number(row.contribution) > 0 ? pdfMoney(row.contribution) : '-',
        Number(row.flex_allocation) > 0 ? pdfMoney(row.employer_net) : '-',
        Number(row.flex_allocation) > 0 ? pdfMoney(row.annualized || Number(row.employer_net) * 12) : '-',
      ];
    }),
  });

  // ===== PAGE 2: Tax Savings Breakdown =====
  pdf.addPage();
  drawProposalPageChrome(pdf, pageWidth, pageHeight, 'TAX SAVINGS BREAKDOWN', proposal);
  yPos = 24;
  yPos = drawStatCards(pdf, [
    { label: 'Federal (mo / yr)', value: `${pdfMoney(monthlyFederal)} / ${pdfMoney(monthlyFederal * 12)}` },
    { label: 'FICA (mo / yr)', value: `${pdfMoney(monthlyFica)} / ${pdfMoney(monthlyFica * 12)}` },
    { label: 'State (mo / yr)', value: `${pdfMoney(monthlyState)} / ${pdfMoney(monthlyState * 12)}` },
  ], marginX, yPos, contentWidth);

  yPos = drawSectionHeading(pdf, 'Combined Tax Savings', marginX, yPos);
  pdf.setFontSize(9);
  pdf.text(`Monthly Total: ${pdfMoney(monthlyCombined)}      Annual Total: ${pdfMoney(annualCombined)}`, marginX, yPos);
  yPos += 5;
  pdf.text(`Federal ${pdfPercent(federalPct)}   |   FICA ${pdfPercent(ficaPct)}   |   State ${pdfPercent(statePct)}`, marginX, yPos);
  yPos += 8;

  yPos = drawTable(pdf, {
    x: marginX,
    y: yPos,
    pageWidth,
    pageHeight,
    headers: [
      { label: 'Employee', align: 'left' },
      { label: 'Federal/mo', align: 'right' },
      { label: 'FICA/mo', align: 'right' },
      { label: 'State/mo', align: 'right' },
      { label: 'Total/mo', align: 'right' },
      { label: 'Total/yr', align: 'right' },
    ],
    colWidths: [90, 45, 45, 45, 45, 45],
    rows: rows.map((row, idx) => {
      const total = (Number(row.federal_tax_savings) || 0) + (Number(row.fica_tax_savings) || 0) + (Number(row.state_tax_savings) || 0);
      return [
        employeeName(row, idx),
        pdfMoney(row.federal_tax_savings),
        pdfMoney(row.fica_tax_savings),
        pdfMoney(row.state_tax_savings),
        pdfMoney(total),
        pdfMoney(total * 12),
      ];
    }),
    totalsRow: ['TOTAL', pdfMoney(monthlyFederal), pdfMoney(monthlyFica), pdfMoney(monthlyState), pdfMoney(monthlyCombined), pdfMoney(annualCombined)],
  });

  // ===== PAGE 3: Your Financial Impact (Employer side) =====
  pdf.addPage();
  drawProposalPageChrome(pdf, pageWidth, pageHeight, 'YOUR FINANCIAL IMPACT', proposal);
  yPos = 24;

  const monthlyGrossBenefit = monthlyFica - monthlyFee;
  const monthlyNetBenefit = monthlyGrossBenefit - monthlyContrib;
  const annualInvestment = monthlyFee * 12;
  const annualReturn = monthlyNetBenefit * 12;
  const paybackMonths = monthlyNetBenefit > 0 ? annualInvestment / monthlyNetBenefit : 0;

  yPos = drawSectionHeading(pdf, 'Monthly Breakdown', marginX, yPos);
  pdf.setFontSize(9);
  pdf.text(`FICA Savings: ${pdfMoney(monthlyFica)}    - Admin Fee: ${pdfMoney(monthlyFee)}    Gross Benefit: ${pdfMoney(monthlyGrossBenefit)}    - Contrib: ${pdfMoney(monthlyContrib)}    Your Net: ${pdfMoney(monthlyNetBenefit)}`, marginX, yPos);
  yPos += 10;

  yPos = drawSectionHeading(pdf, 'Annual Breakdown', marginX, yPos);
  pdf.setFontSize(9);
  pdf.text(`FICA Savings: ${pdfMoney(monthlyFica * 12)}    - Admin Fee: ${pdfMoney(monthlyFee * 12)}    Gross Benefit: ${pdfMoney(monthlyGrossBenefit * 12)}    - Contrib: ${pdfMoney(monthlyContrib * 12)}    Your Net: ${pdfMoney(monthlyNetBenefit * 12)}`, marginX, yPos);
  yPos += 10;

  yPos = drawTable(pdf, {
    x: marginX,
    y: yPos,
    pageWidth,
    pageHeight,
    headers: [
      { label: 'Employee', align: 'left' },
      { label: 'FICA Savings', align: 'right' },
      { label: 'Fee', align: 'right' },
      { label: 'Contrib', align: 'right' },
      { label: 'Your Net (Monthly)', align: 'right' },
    ],
    colWidths: [110, 45, 40, 40, 45],
    rows: rows.map((row, idx) => [
      employeeName(row, idx),
      pdfMoney(row.fica_tax_savings),
      `-${pdfMoney(row.fee)}`,
      `-${pdfMoney(row.contribution)}`,
      pdfMoney(row.employer_net),
    ]),
    totalsRow: ['TOTAL', pdfMoney(monthlyFica), `-${pdfMoney(monthlyFee)}`, `-${pdfMoney(monthlyContrib)}`, pdfMoney(monthlyNetBenefit)],
  });

  yPos = drawSectionHeading(pdf, 'ROI Calculation', marginX, yPos);
  pdf.setFontSize(9);
  pdf.text(`Annual Investment (admin fees): ${pdfMoney(annualInvestment)}`, marginX, yPos);
  yPos += 5;
  pdf.text(`Annual Return (net savings): ${pdfMoney(annualReturn)}`, marginX, yPos);
  yPos += 5;
  pdf.text(`Payback Period: ${annualInvestment > 0 && monthlyNetBenefit > 0 ? `${paybackMonths.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} months` : 'N/A'}`, marginX, yPos);

  // ===== PAGE 4: Comparison =====
  pdf.addPage();
  drawProposalPageChrome(pdf, pageWidth, pageHeight, 'WITH VS WITHOUT SECTION 125', proposal);
  yPos = 24;
  pdf.setFontSize(8);
  pdf.text('"Without Section 125" is modeled as zero tax savings and zero employer impact. "With Section 125" values are sourced from this proposal\'s computed census data.', marginX, yPos, { maxWidth: contentWidth });
  yPos += 10;

  yPos = drawTable(pdf, {
    x: marginX,
    y: yPos,
    pageWidth,
    pageHeight,
    headers: [
      { label: 'Metric', align: 'left' },
      { label: 'Without', align: 'right' },
      { label: 'With', align: 'right' },
      { label: 'Delta', align: 'right' },
    ],
    colWidths: [90, 60, 60, 60],
    rows: [
      ['Employee Tax Savings / mo', pdfMoney(0), pdfMoney(monthlyTotalTaxSavings), pdfMoney(monthlyTotalTaxSavings)],
      ['Employee Tax Savings / yr', pdfMoney(0), pdfMoney(monthlyTotalTaxSavings * 12), pdfMoney(monthlyTotalTaxSavings * 12)],
      ['Employer Net / mo', pdfMoney(0), pdfMoney(monthlyEmployerNet), pdfMoney(monthlyEmployerNet)],
      ['Employer Net / yr', pdfMoney(0), pdfMoney(monthlyEmployerNet * 12), pdfMoney(monthlyEmployerNet * 12)],
      ['Employer FICA Savings / mo', pdfMoney(0), pdfMoney(monthlyFica), pdfMoney(monthlyFica)],
      ['Employer FICA Savings / yr', pdfMoney(0), pdfMoney(monthlyFica * 12), pdfMoney(monthlyFica * 12)],
    ],
  });

  drawProposalFooter(pdf, pageWidth, pageHeight);

  return pdf;
}

// Master column list for the Employees tab's dedicated PDF export. `width` is
// a relative mm weight scaled down to fit the page (see generateEmployeeListPDF)
// rather than an absolute width — the previous fixed widths summed to more
// than the landscape page's content width and silently cropped trailing
// columns off the page. `total`, when present, drives the totals row; columns
// without one (NW4, F, #, State, etc.) render blank there, same as before.
const EMPLOYEE_LIST_COLUMNS = [
  { key: 'full_name', label: 'Employee', align: 'left', width: 38,
    get: (row, idx) => row.full_name || `Employee ${idx + 1}`,
    total: (rows) => `${rows.length} Employee${rows.length !== 1 ? 's' : ''}` },
  { key: 'email', label: 'Email', align: 'left', width: 42,
    get: (row) => row.email || '-' },
  { key: 'annual_salary', label: 'Annual Salary', align: 'right', width: 22,
    get: (row) => row.annual_salary ? pdfMoney(row.annual_salary) : '-',
    total: (rows) => pdfMoney(rows.reduce((s, r) => s + (Number(r.annual_salary) || 0), 0)) },
  { key: 'nw4', label: 'NW4', align: 'center', width: 12,
    get: (row) => row.nw4 || 'N' },
  { key: 'filing_status_code', label: 'F', align: 'center', width: 10,
    get: (row) => {
      const fs = String(row.filing_status || '').toLowerCase();
      return row.filing_status_code || (fs.startsWith('m') ? 'M' : fs.startsWith('h') ? 'H' : 'S');
    } },
  { key: 'w4_allowances', label: '#', align: 'center', width: 10,
    get: (row) => row.w4_allowances ?? '-' },
  { key: 'w4_step3_total', label: '$', align: 'right', width: 16,
    get: (row) => Number(row.w4_step3_total) > 0 ? pdfMoney(row.w4_step3_total) : '-' },
  { key: 'state', label: 'State', align: 'center', width: 14,
    get: (row) => (row.state || '-').toUpperCase() },
  { key: 'flex_allocation', label: 'Sec125/mo', align: 'right', width: 20,
    get: (row) => Number(row.flex_allocation) > 0 ? pdfMoney(row.flex_allocation) : '-',
    total: (rows) => pdfMoney(rows.reduce((s, r) => s + (Number(r.flex_allocation) || 0), 0)) },
  // Gated on eligibility (flex_allocation), not each figure's own sign — net_flex/
  // employer_net can legitimately be zero or negative and that real number must
  // print, not be hidden as '-'.
  { key: 'net_flex', label: 'Net/mo', align: 'right', width: 18,
    get: (row) => Number(row.flex_allocation) > 0 ? pdfMoney(row.net_flex) : '-',
    total: (rows) => pdfMoney(rows.reduce((s, r) => s + (Number(r.net_flex) || 0), 0)) },
  { key: 'net_flex_annual', label: 'Annual Benefit', align: 'right', width: 20,
    get: (row) => Number(row.flex_allocation) > 0 ? pdfMoney(Number(row.net_flex) * 12) : '-',
    total: (rows) => pdfMoney(rows.reduce((s, r) => s + (Number(r.net_flex) || 0), 0) * 12) },
  { key: 'fee', label: 'Fee', align: 'right', width: 16,
    get: (row) => Number(row.fee) > 0 ? pdfMoney(row.fee) : '-',
    total: (rows) => pdfMoney(rows.reduce((s, r) => s + (Number(r.fee) || 0), 0)) },
  { key: 'contribution', label: 'Contrib', align: 'right', width: 18,
    get: (row) => Number(row.contribution) > 0 ? pdfMoney(row.contribution) : '-',
    total: (rows) => pdfMoney(rows.reduce((s, r) => s + (Number(r.contribution) || 0), 0)) },
  { key: 'employer_net', label: 'Empr Net', align: 'right', width: 20,
    get: (row) => Number(row.flex_allocation) > 0 ? pdfMoney(row.employer_net) : '-',
    total: (rows) => pdfMoney(rows.reduce((s, r) => s + (Number(r.employer_net) || 0), 0)) },
  { key: 'annualized', label: 'Annualized', align: 'right', width: 20,
    get: (row) => Number(row.flex_allocation) > 0 ? pdfMoney(row.annualized || Number(row.employer_net) * 12) : '-',
    total: (rows) => {
      const total = rows.reduce((s, r) => s + (Number(r.annualized) || 0), 0);
      return pdfMoney(total || rows.reduce((s, r) => s + (Number(r.employer_net) || 0), 0) * 12);
    } },
];

/**
 * Generate a PDF export of the admin Employees tab's census report table
 * (same columns/order/sort as the on-screen table and the "Export CSV" button).
 * Columns with no meaningful value across every row are dropped rather than
 * exported as a column of blanks.
 */
export async function generateEmployeeListPDF(proposal, rows = []) {
  const pdf = new jsPDF({ orientation: 'landscape' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginX = 10;

  drawProposalPageChrome(pdf, pageWidth, pageHeight, 'EMPLOYEE LIST', proposal);

  let yPos = 24;
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'bold');
  pdf.text(proposal.company_name || 'Employer', marginX, yPos);
  pdf.setFont(undefined, 'normal');
  pdf.setFontSize(8);
  pdf.text(`${rows.length} employee${rows.length !== 1 ? 's' : ''}`, pageWidth - marginX, yPos, { align: 'right' });
  yPos += 8;

  const visibleColumns = EMPLOYEE_LIST_COLUMNS.filter((col) => rows.some((row, idx) => {
    const value = col.get(row, idx);
    return value !== '-' && value !== undefined && value !== null && value !== '';
  }));
  const availableWidth = pageWidth - marginX * 2;
  const rawTotalWidth = visibleColumns.reduce((sum, col) => sum + col.width, 0);
  const scale = rawTotalWidth > availableWidth ? availableWidth / rawTotalWidth : 1;

  yPos = drawTable(pdf, {
    x: marginX,
    y: yPos,
    pageWidth,
    pageHeight,
    headers: visibleColumns.map((col) => ({ label: col.label, align: col.align })),
    colWidths: visibleColumns.map((col) => col.width * scale),
    rows: rows.map((row, idx) => visibleColumns.map((col) => col.get(row, idx))),
    totalsRow: visibleColumns.map((col) => (col.total ? col.total(rows) : '')),
  });

  drawProposalFooter(pdf, pageWidth, pageHeight);

  return pdf;
}